export const newrelicTrackOrderStoreId = (storeOrderId?: string): void => {
  try {
    // Only run on client side to prevent hydration errors
    if (typeof window === 'undefined') return

    if (!window?.NREUM || !storeOrderId) return
    window.NREUM.setCustomAttribute('storeOrderId', storeOrderId)
  } catch (e) {
    console.error(e)
  }
}

export const newrelicTrackUserAction = (
  actionName: string,
  attributes?: Record<string, unknown>
): void => {
  try {
    // Only run on client side to prevent hydration errors
    if (typeof window === 'undefined') return

    if (!window?.NREUM || !actionName) return
    window.NREUM.addPageAction(actionName, attributes)
  } catch (e) {
    console.error(e)
  }
}

export const reportError = (error: Error | string, attributes?: object) => {
  try {
    if (typeof window !== 'undefined' && window.NREUM) {
      window.NREUM.noticeError(error, attributes)
    }
    // Silently fail if New Relic is not available to avoid infinite loops
  } catch (e) {
    // Only log if it's not related to New Relic availability
    if (typeof window !== 'undefined' && window.NREUM) {
      console.error('Error reporting to New Relic:', e)
    }
  }
}

export const initConsoleErrorTracking = (): void => {
  try {
    if (typeof window === 'undefined') return

    // Override console.error to automatically report to New Relic
    const originalConsoleError = console.error
    console.error = function (...args) {
      // Call original console.error first
      originalConsoleError.apply(console, args)

      // Skip reporting if this is a "New Relic not available" message to avoid infinite loops
      const errorMessage = args
        .map((arg) =>
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        )
        .join(' ')

      if (errorMessage.includes('New Relic not available')) {
        return
      }

      reportError(new Error(errorMessage), { source: 'console.error' })
    }

    // Catch unhandled JavaScript errors
    window.addEventListener('error', (event) => {
      const errorAttributes: Record<string, unknown> = {
        source: 'unhandled-error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      }

      // Enhanced detection for "Unexpected token '<'" errors
      if (event.message && event.message.includes('Unexpected token')) {
        errorAttributes.errorType = 'unexpected-token'
        errorAttributes.url = window.location.href
        errorAttributes.userAgent = navigator.userAgent

        // Try to capture the problematic script URL
        if (
          event.target &&
          (event.target as HTMLElement).tagName === 'SCRIPT'
        ) {
          const scriptElement = event.target as HTMLScriptElement
          errorAttributes.scriptSrc = scriptElement.src
          errorAttributes.scriptType = scriptElement.type
        }
      }

      reportError(event.error || new Error(event.message), errorAttributes)
    })

    // Catch unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      reportError(
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason)),
        { source: 'unhandled-promise-rejection' }
      )
    })
  } catch (e) {
    console.error('Failed to initialize console error tracking:', e)
  }
}
